//* react
import React from 'react';

//* material-ui
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

//* types
import { TemplateVarsData, TemplateVarsProps } from 'types/templates/templates-menu';
import useLocalization from 'hooks/useLocalization';
import { VisuallyHiddenInput } from 'ui-component/system/VisuallyHiddenInput';
import { MediaFile, MediaFileType } from 'types/files';
import { SystemVariable } from 'types/system/system-vars';

//* icons
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import QuickreplyOutlinedIcon from '@mui/icons-material/QuickreplyOutlined';

//* system
import { clickPreventDefaultAndStopPropagation, getFileLinkFromServer } from 'utils/helpers';
import SelectFromGalleryDialog from 'ui-component/display/SelectFromGalleryDialog';
import SystemVarsMenu from 'ui-component/display/SystemVarsMenu';
import VariableInput from './VariableInput';
import { getHeaderMediaFieldLabel } from './template-media-field-label';
import { useSnackbar } from 'notistack';

const TemplateVars: React.FC<TemplateVarsProps> = ({
  varsData,
  setVarsData,
  setFileName,
  chatUser,
  templateErrors,
  handleTemplateErrors,
  disableMediaSelection,
  usePredefinedParams,
  predefinedParamsList,
  predefinedChangeAction,
  predefinedExampleData,
  predefinedSelectedColumns,
  initialMediaFile,
  maxVideoFileSizeBytes,
  extraVariables,
  extraVariablesSectionLabel,
  broadcastWizardParams = false,
}) => {
  //* localization
  const { currentLocale } = useLocalization();
  const { enqueueSnackbar } = useSnackbar();
  const { localization } = React.useMemo(
    () => ({
      localization: currentLocale.templatesMenu,
    }),
    [currentLocale],
  );

  //* data & states

  const [templateMediaLoading, setTemplateMediaLoading] = React.useState<boolean>(false);
  const [chooseFromGallery, setChooseFromGallery] = React.useState<MediaFileType | null>(null);
  const [paramsMenuAnchor, setParamsMenuAnchor] = React.useState<{
    anchor: HTMLElement | null;
    param: TemplateVarsData[0];
    index: number;
  } | null>(null);

  //* params
  const handleTemplateComponentTextParam = React.useCallback(
    (paramName: TemplateVarsData[0]['name'], value: string, paramIndex?: number) => {
      setVarsData((prevList) => {
        const newList = [...prevList];
        const targetParam = newList.find(
          (param, index) =>
            param.name === paramName && (!paramName.includes('buttons') || index === paramIndex),
        );
        if (!targetParam) return prevList;
        targetParam.text = value;
        return newList;
      });
      handleTemplateErrors({ [paramName]: value === '' });
    },
    [handleTemplateErrors, setVarsData],
  );

  const handleParamsMenuAnchor = (
    e: React.MouseEvent<HTMLElement>,
    param: TemplateVarsData[0],
    index: number,
  ) => {
    setParamsMenuAnchor({ anchor: e.currentTarget, param, index });
  };
  const handleCloseParamsMenu = React.useCallback(() => {
    setParamsMenuAnchor(null);
  }, []);
  const handleAddVariableToParam = React.useCallback(
    (variable: SystemVariable) => {
      const varText = chatUser ? variable.valueGetter(chatUser) : variable.varName;
      handleCloseParamsMenu();
      if (!paramsMenuAnchor) return;
      const { param, index } = paramsMenuAnchor;
      handleTemplateComponentTextParam(param.name, (param.text || '') + varText, index);
    },
    [chatUser, handleCloseParamsMenu, handleTemplateComponentTextParam, paramsMenuAnchor],
  );

  //* media
  const uploadTemplateFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    clickPreventDefaultAndStopPropagation(e);
    const file = e.target.files?.[0];
    if (
      file &&
      maxVideoFileSizeBytes != null &&
      file.type.startsWith('video/') &&
      file.size > maxVideoFileSizeBytes
    ) {
      enqueueSnackbar(currentLocale.chat.notifications.uploadFile.exceedsSizeLimit, { variant: 'error' });
      e.target.value = '';
      return null;
    }
    setTemplateMediaLoading(true);
    const fileData = await getFileLinkFromServer(e);
    if (fileData) {
      handleTemplateComponentTextParam('data', fileData.Link);
      if (fileData.Name) setFileName(fileData.Name);
      // handleTemplateComponentTextParam("dataFileName", fileData.fileName);
    }
    setTemplateMediaLoading(false);
    return fileData;
  };
  const onSelectFileFromGallery = (file: MediaFile) => {
    handleTemplateComponentTextParam('data', file.Link);
    setFileName(file.Name);
    setChooseFromGallery(null);
  };

  return (
    <Box>
      {varsData.length > 0 && (
        <Stack direction={'column'} spacing={broadcastWizardParams ? 1 : 1.25}>
          {!broadcastWizardParams ? (
            <Typography fontSize={14} fontWeight={600}>
              {localization.componentsTitle}
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {localization.componentsTitle}
            </Typography>
          )}

          {varsData.map((param, index) => {
            const prevParam = varsData[index - 1];

            return (
              <React.Fragment key={`${param.name}`}>
                {param.name.replace(/\d+/g, '') !== prevParam?.name.replace(/\d+/g, '') &&
                !broadcastWizardParams ? (
                  <Typography fontSize={14} fontWeight={600}>
                    {param.name === 'data'
                      ? localization.header
                      : String(
                          localization[
                            param.name.replace(/\d+/g, '').slice(0, -1) as keyof typeof localization
                          ] || '',
                        )}
                  </Typography>
                ) : null}
                <Box
                  sx={
                    broadcastWizardParams
                      ? {
                          minHeight: param.name === 'data' ? 60 : 44,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }
                      : undefined
                  }
                >
                  {usePredefinedParams ? (
                    <VariableInput
                      param={param}
                      templateMediaLoading={templateMediaLoading}
                      uploadTemplateFile={uploadTemplateFile}
                      predefinedParamsList={predefinedParamsList!}
                      predefinedChangeAction={predefinedChangeAction!}
                      index={index}
                      handleTemplateComponentTextParam={handleTemplateComponentTextParam}
                      predefinedExampleData={predefinedExampleData!}
                      predefinedSelectedColumns={predefinedSelectedColumns}
                      setFileName={setFileName}
                      broadcastWizardParams={broadcastWizardParams}
                    />
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label={
                        param.name === 'data'
                          ? getHeaderMediaFieldLabel(param, localization.singleTemplate.media)
                          : `{{${param.name.replaceAll(/\D/g, '')}}}`
                      }
                      value={
                        disableMediaSelection && param.name === 'data' && initialMediaFile
                          ? initialMediaFile || ''
                          : param.text
                      }
                      onChange={(e) => handleTemplateComponentTextParam(param.name, e.target.value, index)}
                      error={templateErrors?.[param.name]}
                      slotProps={{
                        input: {
                          endAdornment:
                            param.name === 'data' ? (
                              !disableMediaSelection && (
                                <InputAdornment position="end">
                                  {templateMediaLoading ? (
                                    <CircularProgress size={20} color="secondary" />
                                  ) : (
                                    <React.Fragment>
                                      <IconButton
                                        size="small"
                                        component="label"
                                        aria-label={localization.singleTemplate.uploadFileOrUseLink}
                                      >
                                        <FileUploadOutlinedIcon />
                                        <VisuallyHiddenInput
                                          type="file"
                                          accept={
                                            param.label === 'image'
                                              ? 'image/*'
                                              : param.label === 'document'
                                                ? '.docx,.doc, .sls,.xlsx,.pdf,.zip'
                                                : param.label === 'video'
                                                  ? 'video/*'
                                                  : undefined
                                          }
                                          onChange={uploadTemplateFile}
                                          id="template-header-media-upload"
                                        />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          clickPreventDefaultAndStopPropagation(e);
                                          setChooseFromGallery(param.label);
                                        }}
                                        aria-label="Choose from gallery"
                                      >
                                        <CollectionsOutlinedIcon />
                                      </IconButton>
                                    </React.Fragment>
                                  )}
                                </InputAdornment>
                              )
                            ) : (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    clickPreventDefaultAndStopPropagation(e);
                                    handleParamsMenuAnchor(e, param, index);
                                  }}
                                  aria-label="Insert variable"
                                >
                                  <QuickreplyOutlinedIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                        },
                      }}
                      disabled={disableMediaSelection && param.name === 'data'}
                    />
                  )}
                </Box>
              </React.Fragment>
            );
          })}
        </Stack>
      )}

      {/* System params menu */}
      <SystemVarsMenu
        anchorEl={paramsMenuAnchor?.anchor}
        open={Boolean(paramsMenuAnchor?.anchor)}
        handleMenuClose={handleCloseParamsMenu}
        chatUser={chatUser}
        onItemClick={handleAddVariableToParam}
        extraVariables={extraVariables}
        extraVariablesSectionLabel={extraVariablesSectionLabel}
      />

      {/* Select from gallery */}
      <SelectFromGalleryDialog
        open={Boolean(chooseFromGallery)}
        mediaType={chooseFromGallery ?? 'image'}
        onClose={() => setChooseFromGallery(null)}
        onSelectFile={onSelectFileFromGallery}
      />
    </Box>
  );
};

export default TemplateVars;
